import React, { useState } from "react";
import { StyleSheet, ScrollView, View, Alert, Platform } from "react-native";
import { router, useLocalSearchParams } from "expo-router";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import { useTheme } from "@/hooks/useTheme";
import { MaterialIcons } from "@expo/vector-icons";

export default function PhotoScreen() {
  const { colors } = useTheme();
  const { session } = useLocalSearchParams();
  const [photoTaken, setPhotoTaken] = useState(false);

  const handleTakePhoto = () => {
    // TODO: Implement camera functionality
    Alert.alert("Take Photo", "Camera functionality will be implemented here");
    setPhotoTaken(true);
  };

  const handleChooseFromLibrary = () => {
    // TODO: Implement photo library selection
    Alert.alert("Choose Photo", "Photo library selection will be implemented here");
    setPhotoTaken(true);
  };

  return (
    <ScrollView style={[styles.container, { backgroundColor: colors.background }]}>
      <View style={styles.content}>
        <ThemedText style={[styles.title, { color: colors.text }]}>Profile Photo</ThemedText>
        <ThemedText style={[styles.subtitle, { color: colors.textSecondary }]}>
          Take or upload a clear photo for your profile
        </ThemedText>

        <View
          style={[
            styles.photoContainer,
            {
              backgroundColor: colors.card,
              borderColor: colors.border,
              borderWidth: 2,
              borderStyle: "dashed",
            },
          ]}
        >
          {photoTaken ? (
            <View style={styles.photoTaken}>
              <MaterialIcons name="check-circle" size={48} color={colors.emerald?.text || colors.primary} />
              <ThemedText style={[styles.photoTakenText, { color: colors.emerald?.text || colors.primary }]}>
                Photo uploaded successfully!
              </ThemedText>
              <Button
                text="Retake Photo"
                iconPosition="right"
                onPress={() => setPhotoTaken(false)}
                icon="refresh"
                iconSize={16}
                style={[
                  styles.retakeButton,
                  { backgroundColor: "transparent", borderColor: colors.border, borderWidth: 1 },
                ]}
                textStyle={{ color: colors.text, fontSize: 14 }}
              />
            </View>
          ) : (
            <View style={styles.photoPlaceholder}>
              <MaterialIcons name="photo-camera" size={48} color={colors.placeholder} />
              <ThemedText style={[styles.placeholderText, { color: colors.textSecondary }]}>
                No photo selected
              </ThemedText>
              <ThemedText style={[styles.placeholderHint, { color: colors.placeholder }]}>
                Take a clear, well-lit photo of your face
              </ThemedText>
            </View>
          )}
        </View>

        <View style={styles.buttonGroup}>
          <Button
            text="Take Photo"
            iconPosition="right"
            onPress={handleTakePhoto}
            icon="camera"
            iconSize={18}
            iconColor="white"
            style={[styles.photoButton, { backgroundColor: colors.primary }]}
            textStyle={{ color: "white", fontSize: 16 }}
          />

          <Button
            text="Choose from Library"
            onPress={handleChooseFromLibrary}
            icon="image-multiple"
            iconSize={18}
            iconColor={colors.text}
            style={[
              styles.photoButton,
              {
                backgroundColor: "transparent",
                borderColor: colors.border,
                borderWidth: 1,
              },
            ]}
            textStyle={{ color: colors.text, fontSize: 16 }}
          />
        </View>

        <View style={[styles.guidelines, { backgroundColor: colors.card, borderColor: colors.border }]}>
          <ThemedText style={[styles.guidelinesTitle, { color: colors.text }]}>Photo Guidelines</ThemedText>
          <View style={styles.guidelinesList}>
            {[
              "Face should be clearly visible",
              "Good lighting, no shadows",
              "Look directly at the camera",
              "Remove sunglasses or hats",
              "Plain background preferred",
            ].map((guideline, index) => (
              <View key={index} style={styles.guidelineItem}>
                <MaterialIcons name="check" size={16} color={colors.emerald?.text || colors.primary} />
                <ThemedText style={[styles.guidelineText, { color: colors.textSecondary }]}>{guideline}</ThemedText>
              </View>
            ))}
          </View>
        </View>

        <Button
          text="Continue"
          onPress={() => router.push(`/sign-up/${session}/verify`)}
          icon="arrow-right"
          iconPosition="right"
          iconSize={18}
          iconColor="white"
          style={[
            styles.continueButton,
            {
              backgroundColor: photoTaken ? colors.primary : colors.border,
              opacity: photoTaken ? 1 : 0.5,
            },
          ]}
          textStyle={{ color: "white", fontSize: 16 }}
          enabled={photoTaken}
        />
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    padding: 20,
  },
  title: {
    fontSize: 24,
    fontWeight: "bold",
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 16,
    marginBottom: 32,
  },
  photoContainer: {
    borderRadius: 12,
    padding: 32,
    alignItems: "center",
    justifyContent: "center",
    minHeight: 200,
    marginBottom: 24,
  },
  photoPlaceholder: {
    alignItems: "center",
    gap: 12,
  },
  placeholderText: {
    fontSize: 16,
    fontWeight: "500",
  },
  placeholderHint: {
    fontSize: 14,
    textAlign: "center",
  },
  photoTaken: {
    alignItems: "center",
    gap: 16,
  },
  photoTakenText: {
    fontSize: 16,
    fontWeight: "600",
  },
  retakeButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
  },
  buttonGroup: {
    gap: 12,
    marginBottom: 24,
  },
  photoButton: {
    borderRadius: 8,
    minHeight: 48,
  },
  guidelines: {
    borderRadius: 12,
    borderWidth: 1,
    padding: 16,
    marginBottom: 32,
  },
  guidelinesTitle: {
    fontSize: 16,
    fontWeight: "600",
    marginBottom: 12,
  },
  guidelinesList: {
    gap: 8,
  },
  guidelineItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  guidelineText: {
    fontSize: 14,
  },
  continueButton: {
    borderRadius: 8,
    minHeight: 48,
  },
});
